import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  Button
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API_BASE_URL } from '../../../config/config';

// Friendly labels for the raw stop/start reason values stored on the session log.
const REASON_LABELS = {
  sensor_logic: 'Sensor Logic',
  pressurefail: 'Pressure Error',
  'pressurefail-db': 'Pressure Error',
  maxduration: 'Max Runtime',
  trialretry: 'Pressure Trial',
  linked_motor: 'Linked Motor',
  trigger_sensor: 'Trigger Sensor',
  manual: 'Manual',
  app: 'Manual',
  hardware_confirmation: 'Hardware',
  orphan_cleanup: 'Cleanup',
  unknown: 'Unknown',
};
const formatReason = (r) => (r ? (REASON_LABELS[r] || r) : '—');

const VirtualMotorLogsModal = ({ motor, deviceCode, open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Date selections
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());

  // Log data
  const [todayLogs, setTodayLogs] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [monthlyLogs, setMonthlyLogs] = useState([]);

  // Statistics
  const [todayStats, setTodayStats] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);
  const [yearSummary, setYearSummary] = useState(null);

  const [loading, setLoading] = useState({ today: false, daily: false, monthly: false });

  // Fetch Today's Logs (or specific date)
  const fetchTodayLogs = async () => {
    setLoading(prev => ({ ...prev, today: true }));
    try {
      const token = localStorage.getItem('token');
      const isToday = selectedDate.isSame(dayjs(), 'day');

      // Use /today endpoint for current day, /date endpoint for other dates
      const endpoint = isToday
        ? `${API_BASE_URL}api/virtual/motor-logs/${motor._id}/today`
        : `${API_BASE_URL}api/virtual/motor-logs/${motor._id}/date`;

      const params = isToday ? {} : { date: selectedDate.format('YYYY-MM-DD') };

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        const data = response.data.data;
        setTodayLogs(data.sessions || []);
        setTodayStats(isToday ? data.todayStats : data.dateStats);
      }
    } catch (error) {
      console.error('Error fetching today logs:', error);
      setTodayLogs([]);
      setTodayStats(null);
    } finally {
      setLoading(prev => ({ ...prev, today: false }));
    }
  };

  // Fetch Daily Logs (aggregated by day for selected month)
  const fetchDailyLogs = async () => {
    setLoading(prev => ({ ...prev, daily: true }));
    try {
      const token = localStorage.getItem('token');
      const currentYear = dayjs().year();

      const response = await axios.get(
        `${API_BASE_URL}api/virtual/motor-logs/${motor._id}/daily`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            year: currentYear,
            month: selectedMonth
          }
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setDailyLogs(data.dailyStats || []);
        setMonthSummary(data.monthSummary);
      }
    } catch (error) {
      console.error('Error fetching daily logs:', error);
      setDailyLogs([]);
      setMonthSummary(null);
    } finally {
      setLoading(prev => ({ ...prev, daily: false }));
    }
  };

  // Fetch Monthly Logs (aggregated by month for selected year)
  const fetchMonthlyLogs = async () => {
    setLoading(prev => ({ ...prev, monthly: true }));
    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(
        `${API_BASE_URL}api/virtual/motor-logs/${motor._id}/monthly`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: selectedYear }
        }
      );

      if (response.data.success) {
        const data = response.data.data;
        setMonthlyLogs(data.monthlyStats || []);
        setYearSummary(data.yearSummary);
      }
    } catch (error) {
      console.error('Error fetching monthly logs:', error);
      setMonthlyLogs([]);
      setYearSummary(null);
    } finally {
      setLoading(prev => ({ ...prev, monthly: false }));
    }
  };

  useEffect(() => {
    if (motor && open) fetchTodayLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor, selectedDate, open]);

  useEffect(() => {
    if (motor && open) fetchDailyLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor, selectedMonth, open]);

  useEffect(() => {
    if (motor && open) fetchMonthlyLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motor, selectedYear, open]);

  const refreshAll = () => {
    fetchTodayLogs();
    fetchDailyLogs();
    fetchMonthlyLogs();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          m: { xs: 0, sm: 2 }, // Remove margins on mobile
        }
      }}
    >
      <DialogTitle sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 1 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant={isMobile ? "subtitle1" : "h5"}
            sx={{ fontWeight: 700, color: '#1a237e', mr: 1 }}
          >
            {motor?.name} Logs
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh All">
              <IconButton onClick={refreshAll} size="small" sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                <RefreshIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" color="primary" onClick={onClose} size="small" sx={{ minWidth: 0, px: 1 }}>
              Close
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, md: 3 }, pt: { xs: 1, md: 2 }, overflowX: 'hidden' }}>
        {/* Statistics Cards */}
        {todayStats && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Sessions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, color: '#1a237e' }}>
                    {todayStats.totalSessions || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Total Duration
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, color: '#0d47a1' }}>
                    {todayStats.totalRunDurationFormatted || '0s'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Average Duration
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, color: '#e65100' }}>
                    {todayStats.averageRunDurationFormatted || '0s'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ bgcolor: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                    Status
                  </Typography>
                  <Chip
                    label={todayStats.currentlyRunning > 0 ? 'Running' : 'Stopped'}
                    sx={{
                      bgcolor: todayStats.currentlyRunning > 0 ? '#e8f5e9' : '#f5f5f5',
                      color: todayStats.currentlyRunning > 0 ? '#2e7d32' : '#757575',
                      fontWeight: 700,
                      borderRadius: 1.5
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Three Column Layout */}
        <Grid container spacing={{ xs: 3, md: 3 }}>
          {/* Today's Log */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600 }}>
                  {selectedDate.isSame(dayjs(), 'day') ? "Today's Log" : "Date Log"}
                </Typography>
                <Tooltip title="Download">
                  <IconButton size="small" disabled={todayLogs.length === 0} sx={{ color: '#1976d2' }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      sx: { mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                    }
                  }}
                />
              </LocalizationProvider>

              <TableContainer
                component={Paper}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  maxHeight: { xs: 350, md: 450 },
                  overflowY: 'auto',
                  width: '100%',
                  overflowX: 'hidden',
                  boxShadow: 'none'
                }}
              >
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '8%', p: 1.5, bgcolor: '#f8f9fa' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%', p: 1.5, bgcolor: '#f8f9fa' }}>ON</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%', p: 1.5, bgcolor: '#f8f9fa' }}>OFF</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '22%', p: 1.5, bgcolor: '#f8f9fa' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '30%', p: 1.5, bgcolor: '#f8f9fa' }}>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading.today ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : todayLogs.length > 0 ? (
                      todayLogs.map((log, idx) => (
                        <TableRow key={log._id || idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.serialNo || idx + 1}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.startTime || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.stopTime ? log.stopTime : (
                              <Chip label="Running" size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 500 }}>
                            {log.runDurationFormatted || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.isCurrentlyRunning
                              ? <Chip label={formatReason(log.triggeredBy)} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                              : formatReason(log.stopReason)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No logs for this date
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </motion.div>
          </Grid>

          {/* Daily Log */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600 }}>
                  Daily Log
                </Typography>
                <Tooltip title="Download">
                  <IconButton size="small" disabled={dailyLogs.length === 0} sx={{ color: '#1976d2' }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {[...Array(12).keys()].map((m) => (
                    <MenuItem key={m + 1} value={m + 1}>
                      {dayjs(new Date(0, m)).format('MMMM')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TableContainer
                component={Paper}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  maxHeight: { xs: 350, md: 450 },
                  overflowY: 'auto',
                  width: '100%',
                  overflowX: 'hidden',
                  boxShadow: 'none'
                }}
              >
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%', p: 1.5, bgcolor: '#f8f9fa' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%', p: 1.5, bgcolor: '#f8f9fa' }}>Runs</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '40%', p: 1.5, bgcolor: '#f8f9fa' }}>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading.daily ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : dailyLogs.length > 0 ? (
                      dailyLogs.map((log, idx) => (
                        <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.dateFormatted || dayjs(log.date).format('D MMM')}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.totalSessions || 0}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 500 }}>
                            {log.totalRunDurationFormatted || '0s'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No logs for this month
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </motion.div>
          </Grid>

          {/* Monthly Log */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600 }}>
                  Monthly Log
                </Typography>
                <Tooltip title="Download">
                  <IconButton size="small" disabled={monthlyLogs.length === 0} sx={{ color: '#1976d2' }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  {[...Array(5).keys()].map((idx) => {
                    const year = dayjs().year() - idx;
                    return <MenuItem key={year} value={year}>{year}</MenuItem>;
                  })}
                </Select>
              </FormControl>

              <TableContainer
                component={Paper}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  maxHeight: { xs: 350, md: 450 },
                  overflowY: 'auto',
                  width: '100%',
                  overflowX: 'hidden',
                  boxShadow: 'none'
                }}
              >
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: '40%', p: 1.5, bgcolor: '#f8f9fa' }}>Month</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%', p: 1.5, bgcolor: '#f8f9fa' }}>Runs</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '40%', p: 1.5, bgcolor: '#f8f9fa' }}>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading.monthly ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : monthlyLogs.length > 0 ? (
                      monthlyLogs.map((log, idx) => (
                        <TableRow key={idx} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.monthName || dayjs().month(log.month - 1).format('MMMM')}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem' }}>
                            {log.totalSessions || 0}
                          </TableCell>
                          <TableCell sx={{ p: 1.5, fontSize: '0.85rem', fontWeight: 500 }}>
                            {log.totalRunDurationFormatted || '0s'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No logs for this year
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </motion.div>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default VirtualMotorLogsModal;
